/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { localDate } from "../WorkDay/shared/dates";
import type { LinkView } from "./types";

type Props = { token: string; view: LinkView; onChanged: () => void };

const dayLabel = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

// Open shifts in their roles, to claim — a manager approves.
export const OpenShifts = defineComponent<Props>({
  name: "OpenShifts",
  props: ["token", "view", "onChanged"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const busy = ref<number | null>(null);

    const pending = (shiftId: number) => props.view.requests.find((r) => r.shift_id === shiftId && r.kind === "claim" && r.status === "pending");

    const claim = async (shiftId: number) => {
      busy.value = shiftId;
      try {
        await trpc.teamLink.requestShift.mutate({ token: props.token, shift_id: shiftId, kind: "claim", today: localDate() });
        toast.showToast(t("teamlink.page.claim_sent"), { type: "success" });
        props.onChanged();
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("teamlink.page.failed"), { type: "error" });
      } finally {
        busy.value = null;
      }
    };

    const withdraw = async (id: number) => {
      await trpc.teamLink.cancelShiftRequest.mutate({ token: props.token, id });
      props.onChanged();
    };

    return () =>
      props.view.open_shifts.length === 0 ? null : (
        <section class="space-y-2">
          <h2 class="px-1 text-sm font-semibold uppercase tracking-wide text-base-content/60">{t("teamlink.page.open_shifts")}</h2>
          {props.view.open_shifts.map((s) => {
            const req = pending(s.id);
            return (
              <div key={s.id} class="rounded-2xl border border-warning/40 bg-base-100 p-4">
                <div class="flex items-center gap-3 rounded-xl bg-base-200 p-3">
                  <span class="material-symbols-outlined text-base-content/60">calendar_month</span>
                  <div>
                    <div class="font-semibold">{`${dayLabel(s.shift_date)}, ${s.start_time} – ${s.end_time}`}</div>
                    <div class="text-sm text-base-content/60">{s.role_name}</div>
                  </div>
                </div>
                <p class="mt-2 text-center text-xs text-base-content/60">{t("teamlink.page.needs_approval")}</p>
                {req ? (
                  <div class="mt-2 flex items-center justify-between gap-2">
                    <span class="badge badge-warning badge-outline">{t("teamlink.page.claim_pending")}</span>
                    <button class="btn btn-ghost btn-xs" onClick={() => withdraw(req.id)}>
                      {t("teamlink.page.cancel")}
                    </button>
                  </div>
                ) : (
                  <button class="btn btn-primary btn-block mt-2" disabled={busy.value === s.id} onClick={() => claim(s.id)}>
                    {t("teamlink.page.claim")}
                  </button>
                )}
              </div>
            );
          })}
        </section>
      );
  },
});
